/**
 * Monday's label colours, FIXED, as the boards' own column settings define
 * them. A Monday status word keeps Monday's hex on every eMerge screen that
 * shows it. Board-agnostic: any board imports a MondayColumn and a word.
 *
 * SOURCE: monday-psql-sync/board-columns-full.json, a Monday API dump of each
 * board's columns (settings_str.labels / labels_colors) dated 2026-08-06.
 * Ported verbatim from backend/src/services/mondayMirror/labelColors.js
 * (label index, word, hex, Monday's var_name).
 */

export interface MondayLabel {
  index: number;
  word: string;
  hex: string;
  varName: string;
  duplicate?: boolean;
}

export interface MondayColumn {
  board: string;
  column: string;
  title: string;
  labels: readonly MondayLabel[];
}

export const MONDAY_DUMP = Object.freeze({
  file: 'monday-psql-sync/board-columns-full.json',
  dated: '2026-08-06',
});

/** UMEDs board 8146038673, column status_1_mkn2hq64 "Status (UMED)". */
export const UMED_STATUS: MondayColumn = Object.freeze({
  board: '8146038673',
  column: 'status_1_mkn2hq64',
  title: 'Status (UMED)',
  labels: Object.freeze([
    { index: 0, word: 'SFS CORRECTION', hex: '#fdab3d', varName: 'orange' },
    { index: 1, word: 'READY TO SUBMIT', hex: '#00c875', varName: 'green-shadow' },
    { index: 2, word: 'REVERSAL', hex: '#df2f4a', varName: 'red-shadow' },
    { index: 3, word: 'PAID IN FULL CORRECTION', hex: '#007eb5', varName: 'blue-links' },
    { index: 4, word: 'REVERSAL RESUB', hex: '#9d50dd', varName: 'purple' },
    { index: 6, word: 'UMED SUBMITTED TO DCF', hex: '#ff5ac4', varName: 'light-pink' },
    { index: 7, word: 'BALANCE WO', hex: '#ff7575', varName: 'sunset' },
    { index: 8, word: 'APPROVED', hex: '#cab641', varName: 'mustered' },
    { index: 9, word: 'SFS CORRECTION COMPLETED', hex: '#ff6d3b', varName: 'dark-orange' },
    { index: 10, word: 'PAID IN FULL', hex: '#333333', varName: 'soft-black' },
    { index: 11, word: 'SFS PORTFOLIO', hex: '#bb3354', varName: 'dark-red' },
    // The board carries PROBLEM ACCOUNT twice (labels 12 and 16); label 16 (yellow) is the one in use.
    { index: 12, word: 'PROBLEM ACCOUNT', hex: '#ff007f', varName: 'dark-pink', duplicate: true },
    { index: 13, word: 'REVERSAL COMPLETE', hex: '#faa1f1', varName: 'bubble' },
    { index: 14, word: 'REVERSAL NOT APPROVED', hex: '#784bd1', varName: 'dark-purple' },
    { index: 15, word: 'DCF RESUB NEEDED', hex: '#9cd326', varName: 'lime-green' },
    { index: 16, word: 'PROBLEM ACCOUNT', hex: '#ffcb00', varName: 'yellow' },
    { index: 17, word: 'NEEDS MCD RECERT', hex: '#757575', varName: 'trolley-grey' },
    { index: 18, word: 'DECEASED', hex: '#7f5347', varName: 'brown' },
    { index: 19, word: 'NOCA-NO FLMMIS', hex: '#66ccff', varName: 'turquoise' },
    { index: 101, word: 'SFS CORRECTION-FLMMIS ISSUE', hex: '#579bfc', varName: 'bright-blue' },
    { index: 102, word: 'NO LONGER QUALIFIES', hex: '#ffadad', varName: 'peach' },
  ] as MondayLabel[]),
});

/** Finance board 8696202069, column color_mkvnc88t "INVOICE CREATED". */
export const FINANCE_INVOICE_CREATED: MondayColumn = Object.freeze({
  board: '8696202069',
  column: 'color_mkvnc88t',
  title: 'INVOICE CREATED',
  labels: Object.freeze([
    { index: 0, word: 'HOLD', hex: '#fdab3d', varName: 'orange' },
    { index: 1, word: 'YES', hex: '#00c875', varName: 'green-shadow' },
  ] as MondayLabel[]),
});

/** SNF Facilities board 9697803348 (the TM board), column status "Status". Index 5 is the blank label. */
export const SNF_FACILITIES_STATUS: MondayColumn = Object.freeze({
  board: '9697803348',
  column: 'status',
  title: 'Status',
  labels: Object.freeze([
    { index: 0, word: 'COLD', hex: '#fdab3d', varName: 'orange' },
    { index: 1, word: 'ACTIVE', hex: '#00c875', varName: 'green-shadow' },
    { index: 2, word: 'DROPPED', hex: '#df2f4a', varName: 'red-shadow' },
    { index: 3, word: 'INTERESTED', hex: '#007eb5', varName: 'blue-links' },
    { index: 4, word: 'DONT BUG', hex: '#e484bd', varName: 'kids' },
  ] as MondayLabel[]),
});

/** SNF Facilities board 9697803348, column color_mkwb5drk "Credentialing". */
export const SNF_CREDENTIALING: MondayColumn = Object.freeze({
  board: '9697803348',
  column: 'color_mkwb5drk',
  title: 'Credentialing',
  labels: Object.freeze([
    { index: 0, word: 'PENDING', hex: '#fdab3d', varName: 'orange' },
    { index: 1, word: 'COMPLETED', hex: '#00c875', varName: 'green-shadow' },
    { index: 2, word: 'MISSING', hex: '#df2f4a', varName: 'red-shadow' },
    { index: 3, word: 'N/A', hex: '#007eb5', varName: 'blue-links' },
  ] as MondayLabel[]),
});

/** Scheduling board 8204775036, column status_mkm1wmrv "SCHEDULING". */
export const SCHEDULING_STATUS: MondayColumn = Object.freeze({
  board: '8204775036',
  column: 'status_mkm1wmrv',
  title: 'SCHEDULING',
  labels: Object.freeze([
    { index: 0, word: 'NEEDS SCHEDULED', hex: '#fdab3d', varName: 'orange' },
    { index: 1, word: 'SCHEDULED', hex: '#9d50dd', varName: 'purple' },
    { index: 2, word: 'COMPLETED VISIT', hex: '#00c875', varName: 'green-shadow' },
    { index: 3, word: 'NEED INFORMATION', hex: '#df2f4a', varName: 'red-shadow' },
    { index: 4, word: 'BRAND NEW FACILITY', hex: '#ff5ac4', varName: 'light-pink' },
    { index: 6, word: 'DROPPED FACILITY', hex: '#bb3354', varName: 'dark-red' },
    { index: 7, word: 'CLOSED DOWN', hex: '#579bfc', varName: 'bright-blue' },
    { index: 8, word: 'RESCHEDULE', hex: '#cab641', varName: 'mustered' },
  ] as MondayLabel[]),
});

/** Scheduling board 8204775036, column color_mktmcajc "Manifest Uploaded?". */
export const SCHEDULING_MANIFEST_UPLOADED: MondayColumn = Object.freeze({
  board: '8204775036',
  column: 'color_mktmcajc',
  title: 'Manifest Uploaded?',
  labels: Object.freeze([
    { index: 0, word: 'PENDING UPLOAD', hex: '#fdab3d', varName: 'orange' },
    { index: 1, word: 'YES', hex: '#00c875', varName: 'green-shadow' },
    { index: 2, word: 'NO', hex: '#df2f4a', varName: 'red-shadow' },
    { index: 3, word: 'PENDING VERIFICATION', hex: '#007eb5', varName: 'blue-links' },
  ] as MondayLabel[]),
});

/** Monday's empty label ("blank"). Not in the dump: Monday's standard grey for an unset status. */
export const MONDAY_BLANK = Object.freeze({
  word: '',
  hex: '#c4c4c4',
  varName: 'explosive',
  source: 'Monday standard palette (not in the dump)',
});

export const MONDAY_COLUMNS = Object.freeze({
  UMED_STATUS,
  FINANCE_INVOICE_CREATED,
  SNF_FACILITIES_STATUS,
  SNF_CREDENTIALING,
  SCHEDULING_STATUS,
  SCHEDULING_MANIFEST_UPLOADED,
});

/** Same normalisation as the backend's normalizeWord. */
export function normalizeMondayWord(raw: string | null | undefined): string | null {
  return (
    String(raw ?? '')
      .trim()
      .toUpperCase()
      .replace(/\s*-\s*/g, '-')
      .replace(/\s+/g, ' ') || null
  );
}

/** The label of a word in one column; a duplicate yields to the one in use. Unknown -> null, never a guess. */
export function mondayLabel(column: MondayColumn, word: string | null | undefined): MondayLabel | null {
  const w = normalizeMondayWord(word);
  if (!w) return null;
  const hits = column.labels.filter((l) => normalizeMondayWord(l.word) === w);
  return hits.find((l) => !l.duplicate) || hits[0] || null;
}

export function mondayHex(column: MondayColumn, word: string | null | undefined): string | null {
  const l = mondayLabel(column, word);
  return l ? l.hex : null;
}

function rgbOf(hex: string): [number, number, number] {
  let h = String(hex).trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) throw new Error(`Invalid hex colour: ${hex}`);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = rgbOf(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio, 1 to 21. */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export const INK_LIGHT = '#ffffff';
export const INK_DARK = '#1b1b1f';

/** Whichever ink reads better on the fill; bold = neither reaches 4.5:1 (bold-label exception). */
export function chipInk(hex: string): { fg: string; ratio: number; bold: boolean } {
  const light = contrastRatio(hex, INK_LIGHT);
  const dark = contrastRatio(hex, INK_DARK);
  const fg = dark > light ? INK_DARK : INK_LIGHT;
  const ratio = Math.max(light, dark);
  return { fg, ratio, bold: ratio < 4.5 };
}

export function filledChipStyle(hex: string): { background: string; color: string; fontWeight: number } {
  const { fg, bold } = chipInk(hex);
  return { background: hex, color: fg, fontWeight: bold ? 700 : 600 };
}

export function paletteOf(
  column: MondayColumn,
): Array<{ word: string; hex: string; fg: string; ratio: number; bold: boolean; index: number; varName: string }> {
  const seen = new Set<string>();
  const out: Array<{ word: string; hex: string; fg: string; ratio: number; bold: boolean; index: number; varName: string }> = [];
  [...column.labels]
    .sort((a, b) => a.index - b.index)
    .forEach((l) => {
      const key = normalizeMondayWord(l.word);
      if (!key || seen.has(key)) return;
      const pick = mondayLabel(column, l.word);
      if (!pick) return;
      seen.add(key);
      const ink = chipInk(pick.hex);
      out.push({ word: pick.word, hex: pick.hex, fg: ink.fg, ratio: ink.ratio, bold: ink.bold, index: pick.index, varName: pick.varName });
    });
  return out.sort((a, b) => a.index - b.index);
}
